// File: Frontend/src/front/occupancy.tsx
// "שינוי הרכב חדר" — the button above the guest details and the popup behind it.
//
// The stored price only ever describes the composition the deal was imported
// for, so every option here is priced live against the supplier for this deal's
// hotel, dates and rate list. The server returns figures that are already
// post-subsidy and post-Tmurah; nothing here recomputes them, because a price
// worked out twice is a price that can disagree with itself at checkout.

import React, { useCallback, useEffect, useState } from 'react';

export type OccupancyOption = {
  code: string;              // "2,1,0"
  label: string;             // "זוג + ילד"
  adults: number;
  children: number;
  infants: number;
  available: boolean;
  unavailable: boolean;      // could not ask the supplier — not the same as "no room"
  grossPrice: number;
  netPrice: number;
  roomName?: string | null;
  currency?: string | null;
};

export type OccupancySelection = {
  code: string;
  label: string;
  grossPrice: number;
  netPrice: number;
  // The room the supplier actually quoted for this composition. A couple + child
  // is not sold the couple's room, so once the price changes the room shown on
  // the page has to change with it.
  roomName?: string | null;
};

const fmt = (n: number) => Math.round(n).toLocaleString('he-IL');

export const OccupancyButton: React.FC<{
  currentLabel: string;
  onClick: () => void;
}> = ({ currentLabel, onClick }) => (
  <button type="button" className="occ-trigger" onClick={onClick}>
    <span className="occ-trigger__icon" aria-hidden="true">👥</span>
    <span>
      שינוי הרכב חדר
      <span className="occ-trigger__current"> · {currentLabel}</span>
    </span>
  </button>
);

export const OccupancyModal: React.FC<{
  dealVariantId: string;
  selectedCode: string;
  api: (path: string, init?: RequestInit) => Promise<Response>;
  onConfirm: (choice: OccupancySelection) => void;
  onClose: () => void;
}> = ({ dealVariantId, selectedCode, api, onConfirm, onClose }) => {
  const [items, setItems] = useState<OccupancyOption[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Starts on what the page is already showing, so opening the popup and
  // pressing "המשך" without touching anything changes nothing.
  const [chosen, setChosen] = useState<string>(selectedCode);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await api(`/api/front/deals/${encodeURIComponent(dealVariantId)}/occupancies`);
      if (!r.ok) { setErr('לא הצלחנו לטעון את ההרכבים כרגע. נסו שוב בעוד רגע.'); setItems([]); return; }
      const data = await r.json();
      setItems(data?.items ?? []);
    } catch {
      setErr('לא הצלחנו לטעון את ההרכבים כרגע. נסו שוב בעוד רגע.');
      setItems([]);
    }
  }, [api, dealVariantId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const selected = items?.find(i => i.code === chosen) ?? null;
  const canContinue = !!selected && selected.available;

  return (
    <div className="irgon-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="occ-title">
      <div className="irgon-modal">
        {/* Title first, close after — the order Ofir asked for on this popup. */}
        <div className="irgon-modal__header">
          <span id="occ-title" className="irgon-modal__header-text">שינוי הרכב חדר</span>
          <button type="button" className="irgon-modal__accept-btn irgon-modal__accept-btn--header" onClick={onClose}>
            סגור
          </button>
        </div>

        <div className="irgon-modal__body">
          <p className="occ-intro">
            בחרו את הרכב החדר, והמחיר יתעדכן בהתאם. המחיר מוצג לאחר הסבסוד,
            עבור אותו מלון ואותם תאריכים.
          </p>

          {items === null && (
            <div className="occ-loading">
              <span className="occ-spinner" aria-hidden="true" />
              <span>בודקים מחירים לכל הרכב מול המלון…</span>
            </div>
          )}
          {err && <div className="irgon-alert-error">{err}</div>}

          {items !== null && items.length > 0 && (
            <>
              <div className="occ-label" id="occ-label">הרכב חדר</div>
              {/* A list of rows, not a <select>. Firefox draws an open select
                  with page CSS and offset every row's text out of the visible
                  box; three attempts at fixing that from the outside failed,
                  and there is nothing here a native control was giving us that
                  this does not. The price sits on the row because the whole
                  list was priced to build it — making the member open each one
                  to see what it costs would hide work already done. */}
              <div className="occ-list" role="radiogroup" aria-labelledby="occ-label">
                {items.map(i => (
                  <React.Fragment key={i.code}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={i.code === chosen}
                    disabled={!i.available}
                    className={`occ-row${i.code === chosen ? ' is-selected' : ''}${i.available ? '' : ' is-disabled'}`}
                    onClick={() => { if (i.available) setChosen(i.code); }}
                  >
                    <span className="occ-row__label">
                      {i.label}
                      {/* The bracket decides the price, so it belongs on the
                          row being priced rather than in a note underneath the
                          list that nobody reads while comparing. No offered
                          composition has an infant in it — a cot is arranged
                          with the desk — so children are the only bracket. */}
                      {i.children > 0 && (
                        <span className="occ-row__ages">ילדים 2-12</span>
                      )}
                    </span>
                    {/* Both figures on the row. The regular price is what the
                        Tmurah is a discount off, so showing only the discounted
                        one hides what the member is comparing against. */}
                    <span className="occ-row__price">
                      {i.available ? (
                        <>
                          <span className="occ-row__gross">מחיר רגיל {fmt(i.grossPrice)} ₪</span>
                          <span className="occ-row__net">מחיר תמורה {fmt(i.netPrice)} ₪</span>
                        </>
                      ) : 'פנו למרכז ההזמנות'}
                    </span>
                  </button>
                  </React.Fragment>
                ))}
              </div>

              {/* The whole point of the popup: the new price, before committing. */}
              {selected && selected.available && (
                <div className="occ-price">
                  <div className="occ-price__title">סה״כ להרכב שנבחר · {selected.label}</div>
                  <div className="occ-price__row">
                    <span>מחיר רגיל</span>
                    <b>{fmt(selected.grossPrice)} ₪</b>
                  </div>
                  <div className="occ-price__row occ-price__row--net">
                    <span>מחיר תמורה</span>
                    <b>{fmt(selected.netPrice)} ₪</b>
                  </div>
                </div>
              )}

              {selected && !selected.available && (
                <div className="occ-none">
                  אין מחיר להרכב זה באתר — לקבלת מחיר פנו למרכז ההזמנות.
                </div>
              )}
            </>
          )}
        </div>

        <div className="irgon-modal__footer" style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button type="button" className="irgon-modal__accept-btn" onClick={onClose}>ביטול</button>
          <button
            type="button"
            className="irgon-modal__accept-btn"
            disabled={!canContinue}
            style={!canContinue ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            onClick={() => {
              if (!selected) return;
              onConfirm({
                code: selected.code,
                label: selected.label,
                grossPrice: selected.grossPrice,
                netPrice: selected.netPrice,
                roomName: selected.roomName ?? null,
              });
            }}
          >
            המשך
          </button>
        </div>
      </div>
    </div>
  );
};
